import { useEffect, useState } from 'react';
import { compact } from 'lodash-es';
import { useHistory, useLocation } from 'react-router';
import { Page, Tabs, Typography, Divider, PageContentHeader, Callout, Button, useOverlayContext, ConfirmationModal } from 'seagull';
import { MailSentLiner24 } from 'seagull/icons';
import ExternalLinkButton from "../../components/ExternalLinkButton";
import { routes } from "../../constants";
import { useStore, useStoreActions } from "../../contexts";
import { useEmailAuthCompletion } from "../../hooks/useEmailAuthCompletion";
import { useRestfulWrapper } from "../../hooks/useRestfulWrapper";
import { handleRequest } from "../../infra/utils";
import { EmailOnBehalfSettingsModel } from "../../models/EmailOnBehalfSettingsModel";
import { selectors } from "../../store/state-selectors";
import { helpArticles } from "../../utils/helpArticles";
import pluralize from "../../utils/pluralize";
import styles from "./NotificationSettings.module.scss";
import { Tab, Table, isDays, minutesToDays, getRecipientTypesText, reminderColumnDefsMap, reminderHeaderItemsMap, notificationColumnDefs, notificationHeaderItems, getEmailProviderDetails } from "./NotificationSettings.utils";
import { NotificationSettingsTable } from "./NotificationSettingsTable";
import { SendEmailOnBehalfModal } from "./SendEmailOnBehalfModal";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const NotificationSettings = () => {
  const {
    api
  } = useStoreActions();
  const history = useHistory();
  const businessId = useStore(selectors.user.businessId);
  const location = useLocation();
  const [selectedTab, setSelectedTab] = useState(Tab.Vendor);
  const overlayContext = useOverlayContext();
  const [emailOnBehalf, setEmailOnBehalf] = useState(EmailOnBehalfSettingsModel.makeDisconnected());
  const {
    startLoader,
    stopLoader,
    showErrorToast
  } = useOverlayContext();
  const {
    logBI
  } = useStoreActions();
  const fetchData = async () => {
    if (!businessId) {
      history.push(routes.home);
      return {};
    }
    return api.notification.getNotificationSettings(businessId).then(data => {
      setEmailOnBehalf(data.sendOnBehalfOf);
      return data;
    });
  };
  const {
    refresh,
    renderRestfulState
  } = useRestfulWrapper({
    fetch: fetchData
  });
  useEffect(() => {
    var _location$state;
    const selectedTab = (_location$state = location.state) === null || _location$state === void 0 ? void 0 : _location$state.selectedTab;
    if (selectedTab) {
      setSelectedTab(selectedTab);
    }
  }, [location.state]);
  useEmailAuthCompletion({
    businessId,
    onSuccess: async () => {
      await refresh();
      setSelectedTab(Tab.Client);
    },
    onError: (errorMessage, toastId) => {
      showErrorToast(errorMessage, toastId);
      setSelectedTab(Tab.Client);
    },
    onAccessDenied: () => {
      showErrorToast('Failed to connect your email account. Please try again.', 'integrations-access-denied');
      setSelectedTab(Tab.Client);
    }
  });
  const connectEmailOnBehalf = async () => {
    logBI(18500, {
      vendorId: businessId
    });
    overlayContext.showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: _ref => {
        let {
          dismissModal
        } = _ref;
        return /*#__PURE__*/_jsx(SendEmailOnBehalfModal, {
          dismissModal: dismissModal
        });
      }
    });
  };
  const disconnectEmailOnBehalf = async () => {
    const provider = emailOnBehalf.provider();
    if (!provider) return;
    const providerDetails = getEmailProviderDetails(provider);
    overlayContext.showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: _ref2 => {
        let {
          dismissModal
        } = _ref2;
        return /*#__PURE__*/_jsx(ConfirmationModal, {
          skin: "danger",
          title: "Are you sure?",
          textAlignCenter: false,
          content: /*#__PURE__*/_jsxs(Typography, {
            children: ["Once disconnected, emails to clients will be sent from", ' ', /*#__PURE__*/_jsx(Typography, {
              as: "span",
              weight: "bold",
              children: "system@notifications.sayanchor.com"
            })]
          }),
          primaryButtonLabel: "Disconnect",
          secondaryButtonLabel: "Cancel",
          secondaryButtonTestId: "disconnect-modal-cancel-button",
          primaryButtonTestId: "disconnect-modal-primary-button",
          onCloseButtonClick: dismissModal,
          onSecondaryButtonClick: dismissModal,
          onPrimaryButtonClick: async () => {
            if (businessId) {
              startLoader();
              const {
                success
              } = await handleRequest(api.notification.emailOnBehalf.disconnectEmailOnBehalf(businessId));
              if (success) {
                setEmailOnBehalf(EmailOnBehalfSettingsModel.makeDisconnected());
                dismissModal();
                logBI(providerDetails.biCodes.disconnect, {
                  vendorId: businessId
                });
              } else {
                showErrorToast('Failed to disconnect gmail account - please try again', 'disconnect-email-on-behalf-error-toast');
              }
              stopLoader();
            }
          }
        });
      }
    });
  };
  return /*#__PURE__*/_jsxs(Page, {
    header: "Notification Settings",
    subheader: /*#__PURE__*/_jsxs(_Fragment, {
      children: ["Set which notifications you and your clients will receive.", /*#__PURE__*/_jsx(ExternalLinkButton, {
        href: helpArticles.notificationSettings(),
        testId: 'learn-more-link',
        openInNewTab: true,
        className: styles.learnMoreLink,
        underline: "none",
        children: "Learn more"
      })]
    }),
    testId: "notification-settings",
    children: [/*#__PURE__*/_jsx(PageContentHeader, {
      children: /*#__PURE__*/_jsx(Tabs, {
        value: selectedTab,
        onChange: (_, value) => setSelectedTab(value),
        tabs: [{
          component: 'Notifications sent to your team',
          id: Tab.Vendor,
          value: Tab.Vendor,
          testId: "vendor-notifications-tab"
        }, {
          component: 'Notifications sent to your clients',
          id: Tab.Client,
          value: Tab.Client,
          testId: "client-notifications-tab"
        }]
      })
    }), renderRestfulState({
      renderChild: _ref3 => {
        let {
          data,
          loading,
          refresh
        } = _ref3;
        return /*#__PURE__*/_jsxs(_Fragment, {
          children: [!loading && selectedTab === Tab.Client && /*#__PURE__*/_jsx(Callout, {
            icon: /*#__PURE__*/_jsx(MailSentLiner24, {}),
            className: styles.mailCallout,
            testId: 'connect-email-on-behalf-callout',
            variant: "paragraph1",
            withAction: emailOnBehalf.isDisconnected() ? /*#__PURE__*/_jsx(Button, {
              skin: "primary",
              testId: 'connect-email-on-behalf-button',
              onClick: connectEmailOnBehalf,
              size: "small",
              children: "Send emails using my own domain"
            }) : /*#__PURE__*/_jsx(Button, {
              skin: "primary",
              testId: 'disconnect-email-on-behalf-button',
              onClick: disconnectEmailOnBehalf,
              size: "small",
              children: "Disconnect"
            }),
            children: /*#__PURE__*/_jsxs(Typography, {
              testId: 'connect-email-on-behalf-text',
              children: ["Client Notifications are sent from", ' ', /*#__PURE__*/_jsx(Typography, {
                as: "span",
                weight: "bold",
                children: emailOnBehalf.email()
              })]
            })
          }), /*#__PURE__*/_jsx(Typography, {
            testId: "".concat(Table.Notifications, "-table-title"),
            variant: "h6",
            className: styles.tableTitle,
            children: 'Notifications triggered when an event occurs'
          }), /*#__PURE__*/_jsx(NotificationSettingsTable, {
            data: data,
            refresh: refresh,
            tableType: Table.Notifications,
            columnDefs: notificationColumnDefs,
            headerItems: notificationHeaderItems,
            selectedTab: selectedTab,
            getRowItems: _ref4 => {
              let {
                notification,
                text,
                key
              } = _ref4;
              return [{
                value: /*#__PURE__*/_jsx(Typography, {
                  weight: "normal",
                  children: text
                }),
                testId: "".concat(Table.Notifications, "-row-").concat(key, "-title")
              }, {
                value: /*#__PURE__*/_jsx(Typography, {
                  children: notification.recipientTypes.length ? 'Active' : 'Inactive'
                }),
                testId: "".concat(Table.Notifications, "-row-").concat(key, "-status")
              }, {
                value: getRecipientTypesText(notification.recipientTypes, key).map(text => /*#__PURE__*/_jsx(Typography, {
                  as: "div",
                  className: styles.preserveLineBreak,
                  children: text
                }, text)),
                testId: "".concat(Table.Notifications, "-row-").concat(key, "-notified-team-members")
              }];
            }
          }), /*#__PURE__*/_jsx(Divider, {}), /*#__PURE__*/_jsx(Typography, {
            testId: "".concat(Table.Reminders, "-table-title"),
            variant: "h6",
            className: styles.tableTitle,
            children: 'Notifications triggered when an action is delayed'
          }), selectedTab === Tab.Client && /*#__PURE__*/_jsx(Typography, {
            testId: "".concat(Table.Reminders, "-subtitle"),
            skin: "secondary",
            variant: "paragraph1",
            children: 'Notifications are sent at fixed intervals, up to a selected number of retries'
          }), /*#__PURE__*/_jsx(NotificationSettingsTable, {
            data: data,
            refresh: refresh,
            tableType: Table.Reminders,
            columnDefs: reminderColumnDefsMap[selectedTab],
            headerItems: reminderHeaderItemsMap[selectedTab],
            selectedTab: selectedTab,
            getRowItems: _ref5 => {
              let {
                notification,
                text,
                key
              } = _ref5;
              return compact([{
                value: /*#__PURE__*/_jsx(Typography, {
                  weight: "normal",
                  children: text
                }),
                testId: "reminders-row-".concat(key, "-title")
              }, {
                value: /*#__PURE__*/_jsx(Typography, {
                  children: notification.recipientTypes.length ? 'Active' : 'Inactive'
                }),
                testId: "reminders-row-".concat(key, "-status")
              }, {
                value: notification.recipientTypes.length ? isDays(notification.recurring.minutes) ? pluralize('day', minutesToDays(notification.recurring.minutes), true) : pluralize('minute', notification.recurring.minutes, true) : '',
                testId: "reminders-row-".concat(key, "-").concat(selectedTab === Tab.Client ? 'interval' : 'delay')
              }, selectedTab === Tab.Client && {
                value: notification.recipientTypes.length ? notification.recurring.times : '',
                testId: "reminders-row-".concat(key, "-retries")
              }, {
                value: getRecipientTypesText(notification.recipientTypes, key).map(text => /*#__PURE__*/_jsx(Typography, {
                  as: "div",
                  className: styles.preserveLineBreak,
                  children: text
                }, text)),
                testId: "reminders-row-".concat(key, "-notified-team-members")
              }]);
            }
          })]
        });
      }
    })]
  });
};
export default NotificationSettings;